import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import { uploadMedia, getMediaUrl } from 'services/media';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import { withTheme } from '@mui/styles';
import variablePlugin from './tinymce.variables.plugin';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '../IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import Files from 'modules/files';
import Dialog from '@mui/material/Dialog';
import { BUCKET_URL } from 'constants/index';
import FormLabel from '@mui/material/FormLabel';

const FilePicker = ({ onSelect, onClose }) => {
  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>
        Select file
        <IconButton
          sx={{ position: 'absolute', right: 1, top: 1 }}
          icon={<CloseIcon />}
          onClick={onClose}
        />
      </DialogTitle>
      <DialogContent>
        <Files
          filePicker
          onFileSelect={onSelect}
          accept={{
            'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

class RichTextEditor extends React.Component {
  state = {
    loading: true,
    key: 1,
    showPicker: false,
  };

  pickerRef = React.createRef();

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.variables !== this.props.variables) {
      this.setState({
        key: this.state.key + 1, // handle variables prop change and reinit editor
      });
    }
  }

  onClosePick = () => {
    this.setState({ showPicker: false });
  };

  onFileSelect = (file) => {
    const url = BUCKET_URL + file.location;
    this.state.showPicker(url, {
      maxWidth: '100%',
      width: 'auto',
      height: 'auto',
    });
    this.setState({ showPicker: false });
  };

  renderFilePicker = (callback) => {
    this.setState({ showPicker: callback });
  };

  render() {
    const {
      label,
      value,
      onChange,
      innerRef,
      placeholder,
      hasError,
      helpText,
      theme,
      id = 'mce',
      variables,
      height = 400,
      margin = 'normal',
      ...other
    } = this.props;

    const isDark = theme.palette.mode === 'dark';

    return (
      <FormControl
        margin={margin}
        fullWidth
        error={hasError}
        sx={{
          '& .tox-tinymce': {
            'borderColor': hasError ? 'error.main' : 'grey.300',
            '&.focused': {
              borderColor: hasError ? 'error.main' : 'primary.main',
            },
          },
        }}
        {...other}
      >
        {label && <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>}
        <Editor
          tinymceScriptSrc={'/assets/js/tinymce/js/tinymce/tinymce.min.js'}
          key={this.state.key}
          disabled={this.state.loading}
          apiKey="v90a1k43iqyfp5qm7pm4vkc89opy0ptw4sc525pshsvqzvjk"
          init={{
            placeholder,
            height,
            skin: isDark ? 'oxide-dark' : undefined,
            content_css: isDark ? 'dark' : undefined,
            auto_focus: false,
            statusbar: false,
            browser_spellcheck: true,
            relative_urls: false,
            link_assume_external_targets: true,
            urlconverter_callback: function (url, node, on_save, name) {
              return url;
            },
            remove_script_host: false,
            contextmenu: false,
            variable_mapper: variables,
            menubar: false,
            plugins: [
              'advlist',
              'autolink',
              'lists',
              'link',
              'image',
              'charmap',
              'preview',
              'anchor',
              'searchreplace',
              'visualblocks',
              'fullscreen',
              'media',
              'table',
              'code',
              'variable',
            ],
            toolbar:
              'blocks | ' +
              'bold italic forecolor backcolor | underline link linktracking image | alignleft aligncenter ' +
              '| bullist numlist outdent indent | ' +
              'removeformat code fullscreen',
            content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px }
            img { max-width: 100%; height: auto; }
            .variable {
                  cursor: default;
                  background-color: #894FE8;
                  color: #FFF;
                  padding: 2px 10px;
                  border-radius: 14px;
                  font-style: normal;
                  font-size: 14px;
                  display: inline-block;
                  line-height: 1;
                }
            `,
            invalid_elements:
              'form,iframe,script,@[onclick|ondblclick|' +
              'onmousedown|onmouseup|onmouseover|onmousemove|onmouseout|onkeypress|' +
              'onkeydown|onkeyup]',
            extended_valid_elements:
              'a[clicktracking|href|target=_blank|title|rel|class|style]',
            file_picker_types: 'image',
            image_description: false,
            image_advtab: false,
            typeahead_urls: false,
            file_picker_callback: this.renderFilePicker,
            images_upload_handler: function (blobInfo, progress) {
              return uploadMedia(
                blobInfo.blob(),
                undefined,
                blobInfo.filename(),
                progress,
              ).then(({ location }) => getMediaUrl(location));
            },
            paste_preprocess: function (editor, args) {
              let content = args.content;
              // if this is html then remove span with variable class
              if (content.indexOf('<span class="variable">') !== -1) {
                content = content.replace(
                  /<span class="variable">([^<]+)<\/span>/g,
                  '$1',
                );
              }
              args.content = content;
            },
            setup: function (editor) {
              editor.ui.registry.addIcon(
                'linktracking-icon',
                '<svg width="24px" height="24px" viewBox="0 0 24 24"><path d="M20 19.7L3.3 3L2 4.3L5.2 7.5C5.1 8 5 8.5 5 9C5 14.2 12 22 12 22S13.7 20.1 15.4 17.6L18.8 21L20 19.7M12 18.7C7.4 12.5 7 10.4 7 9.3L13.8 16.1C13.3 16.9 12.7 17.7 12 18.7M8.4 5.6L7 4.2C8.2 2.8 10 2 12 2C15.9 2 19 5.1 19 9C19 10.7 18.3 12.6 17.3 14.5L15.8 13C17 10.6 17 9.6 17 9C17 6.2 14.8 4 12 4C10.6 4 9.3 4.6 8.4 5.6M12 6.5C13.4 6.5 14.5 7.6 14.5 9C14.5 9.7 14.2 10.4 13.7 10.9L10.2 7.4C10.6 6.8 11.3 6.5 12 6.5Z" /></svg>',
              );

              editor.ui.registry.addToggleButton('linktracking', {
                icon: 'linktracking-icon',
                tooltip: 'Toggle click tracking',
                onAction: (api) => {
                  const elm = getAnchorElement();
                  if (!elm) {
                    api.setActive(false);
                  } else {
                    const isTracking = elm.getAttribute('clicktracking');
                    if (!isTracking) {
                      elm.setAttribute('clicktracking', 'off');
                      api.setActive(true);
                    } else {
                      elm.removeAttribute('clicktracking');
                      api.setActive(false);
                    }
                  }
                },
                onSetup: (api) => {
                  const nodeChangeHandler = () => {
                    const elm = getAnchorElement();
                    if (elm) {
                      const isTracking = elm.getAttribute('clicktracking');
                      api.setActive(!!isTracking);
                    } else {
                      api.setActive(false);
                    }
                  };
                  editor.on('nodechange', nodeChangeHandler);
                  return () => editor.off('nodechange', nodeChangeHandler);
                },
              });

              const isAnchorElement = function (node) {
                return node.nodeName.toLowerCase() === 'a' && node.href;
              };

              const getAnchorElement = function () {
                const node = editor.selection.getNode();
                return isAnchorElement(node) ? node : null;
              };

              editor.editorManager.PluginManager.add(
                'variable',
                variablePlugin,
              );
              editor.on('init', function (e) {
                editor.windowManager.oldOpen = editor.windowManager.open; // save for later
                editor.windowManager.open = function (t, r) {
                  // replace with our own function
                  const modal = editor.windowManager.oldOpen.apply(this, [
                    t,
                    r,
                  ]); // call original
                  if (t.title === 'Source Code') {
                    const el = document.getElementsByClassName(
                      'tox-dialog__body-content',
                    );
                    const msg = document.createElement('p');
                    msg.classList.add('text-strong');
                    msg.innerText =
                      'Warning! For advanced users only. For security, script & iframe tags are escaped. Do not paste 3rd party code snippet.';
                    el.item(0).prepend(msg);
                  }
                  return modal;
                };
              });

              editor.on('focus', (e) => {
                e.target.editorContainer.classList.toggle('focused');
              });

              editor.on('blur', (e) => {
                e.target.editorContainer.classList.toggle('focused');
              });
            },
          }}
          onEditorChange={onChange}
          ref={innerRef}
          value={value}
          onInit={() => this.setState({ loading: false })}
        />
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
        {this.state.showPicker && (
          <FilePicker onClose={this.onClosePick} onSelect={this.onFileSelect} />
        )}
      </FormControl>
    );
  }
}

RichTextEditor.defaultProps = {
  placeholder: 'Enter your message',
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  innerRef: PropTypes.object,
};

export default withTheme(RichTextEditor);
